import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import NavigationBar from "../../Components/NavigationBar";

export default function BursarApprove() {
  const location = useLocation();
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const FILE_BASE_URL = process.env.REACT_APP_FILE_BASE_URL;

  const applicationIds = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (
      params
        .get("applicationId")
        ?.split(",")
        .map((id) => id.trim())
        .filter(Boolean) || []
    );
  }, [location.search]);

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchApplications = async () => {
      if (applicationIds.length === 0) {
        setApplications([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${API_BASE_URL}/getApplicationsForBursar?applicationId=${applicationIds.join(
            ","
          )}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
          throw new Error("Invalid data format received from server");
        }

        setApplications(data);
      } catch (err) {
        console.error("Error fetching applications:", err);
        setError(err.message || "Failed to fetch applications");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [applicationIds]);

  const handleDecision = async (appId, decision) => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/updatebursarDecision`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ app_ID: appId, decision }),
        }
      );

      if (res.ok) {
        alert(`Application ${appId} ${decision.toLowerCase()}!`);
        setApplications((prevApps) =>
          prevApps.map((app) =>
            app.Application_Id === appId
              ? { ...app, Status: decision === "Accepted" ? 4.1 : 4.2 }
              : app
          )
        );
      } else {
        // Defensive parsing of error response
        const text = await res.text();
        try {
          const error = JSON.parse(text);
          alert("Failed to update decision: " + error.error);
        } catch {
          console.error("Raw error response:", text);
          alert("Failed to update decision. Response was not valid JSON.");
        }
      }
    } catch (err) {
      console.error("Error:", err);
      alert("An error occurred while updating decision.");
    }
  };

  if (loading) {
    return (
      <div>
        <NavigationBar />
        <div className="container mt-4">
          <p>Loading application details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <NavigationBar />
        <div className="container mt-4">
          <div className="alert alert-danger">
            Error loading applications: {error}
          </div>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavigationBar />
      <div>
        <h1 className="page-name">Bursar Approval</h1>

        {applications.length > 0 ? (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Application Id</th>
                <th>Applicant Name</th>
                <th>Award Type</th>
                <th>Status</th>
                <th>Decision</th>
                <th>Application</th>
                <th>Payment Receipt</th>
              </tr>
            </thead>
            <tbody>
              {applications.map((app) => (
                <tr key={app.Application_Id}>
                  <td>{app.Application_Id}</td>
                  <td>{app.Name}</td>
                  <td>{app.Award_Type}</td>
                  <td>
                    {app.Status === "Accepted"
                      ? "Accepted"
                      : app.Status === "Declined"
                      ? "Declined"
                      : app.Status}
                  </td>
                  <td>
                    {app.Status === "Accepted" ? (
                      <Button variant="success" size="sm">
                        Accepted
                      </Button>
                    ) : app.Status === "Declined" ? (
                      <Button variant="danger" size="sm">
                        Declined
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() =>
                            handleDecision(app.Application_Id, "Accepted")
                          }
                        >
                          Accept
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          style={{ marginLeft: "10px" }}
                          onClick={() =>
                            handleDecision(app.Application_Id, "Declined")
                          }
                        >
                          Decline
                        </Button>
                      </>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => {
                        const formattedType = app.Award_Type.replace(
                          /\s+/g,
                          "_"
                        );
                        const fileUrl = `${FILE_BASE_URL}/api/Uploads/ApplicationForms/${app.Application_Id}_${formattedType}.pdf`;
                        window.open(fileUrl, "_blank");
                      }}
                    >
                      View
                    </Button>
                  </td>
                  <td>
                    {app.Payment_Receipt_Receive === 1 ? (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => {
                          const receiptUrl = `${FILE_BASE_URL}/api/Uploads/APCPaymentReceipt/${app.Application_Id}_PaymentReceipt.pdf`;
                          window.open(receiptUrl, "_blank");
                        }}
                      >
                        Download Receipt
                      </Button>
                    ) : (
                      <span className="text-muted">Not available</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="alert alert-info">
            No applications found for the provided IDs.
          </div>
        )}
      </div>
    </div>
  );
}
